import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@kop/ui';

import { AuthScreen, PendingScreen, RegistrationCompletionScreen } from './auth-screen';
import { createWorkspaceAuthAdapter } from './auth-adapter-factory';
import { beginHomeWarmStart, endHomeWarmStart } from '../features/home-warm-start';
import {
  clearSensitiveMemoryCaches,
  scheduleMidnightMemoryCacheClear,
} from '../runtime/sensitive-cache-lifecycle';
import type { AuthAdapter, AuthState, AuthenticatedSession } from './types';
import { normalizeSessionFailureCode } from './session-failure-code';
import { projectVisibleProductSession } from './product-session';
import { isHeadquartersScope } from '../headquarters-site-navigation';
import { workspaceHomeSelectedScopeId } from '../runtime/operational-selected-scope';
import { FeatureLoading } from '../features/feature-skeleton';
import { WorkspaceEntry } from './workspace-entry';
import { MobileSessionLock } from './mobile-session-lock';

const RiskWorkerOpinionPage = lazy(() => import('../features/risk-assessment/risk-worker-opinion-page').then(module => ({ default: module.RiskWorkerOpinionPage })));

const ExternalClaimScreen = lazy(() =>
  import('../features/contractors/external-claim-screen').then((module) => ({
    default: module.ExternalClaimScreen,
  })),
);

/* The worker's own registration page. It is reached by scanning a QR at the
 * 신규 입장 교육 by somebody who has no account and never will, so it answers
 * before the authenticated boundary below is entered at all: the invitation
 * token in the link is the whole credential and App Check attests the app. */
const WorkerRegistrationPage = lazy(() =>
  import('../features/worker-roster/worker-registration-page').then((module) => ({
    default: module.WorkerRegistrationPage,
  })),
);

const OnboardingScreen = lazy(() =>
  import('./onboarding-screen').then((module) => ({ default: module.OnboardingScreen })),
);

const SetupUnavailableScreen = lazy(() =>
  import('./onboarding-screen').then((module) => ({ default: module.SetupUnavailableScreen })),
);

function sessionFailureCopy(code: string) {
  const normalized = normalizeSessionFailureCode(code);
  if (normalized === 'IDENTITY-SESSION-TIMEOUT' || normalized === 'IDENTITY-INITIALIZATION-FAILED')
    return {
      title: '계정 확인을 완료하지 못했습니다.',
      detail: '로그인 정보를 불러오는 과정이 멈췄습니다. 다시 시도해 주세요. 계속되면 새 로그인 창에서 접속해 주세요.',
    };
  if (normalized.startsWith('APPCHECK/') || normalized === 'IDENTITY-APP-CHECK-UNAVAILABLE')
    return {
      title: '브라우저 보안 확인을 완료하지 못했습니다.',
      detail: '계정의 사업장 권한을 확인하기 전에 접속 보안 확인에서 중단됐습니다. 평소 사용하던 브라우저에서도 계속되면 운영 담당자에게 알려 주세요.',
    };
  if (normalized === 'UNAUTHENTICATED')
    return {
      title: '로그인을 다시 해 주세요.',
      detail: '로그인 유지시간이 지나 다시 인증이 필요합니다. 같은 계정으로 로그인하면 계속 사용할 수 있습니다.',
    };
  if (normalized === 'ACCOUNT-SUSPENDED')
    return {
      title: '계정 사용이 중지되어 있습니다.',
      detail: '가입 승인 대기 상태가 아닙니다. 운영 담당자에게 계정 상태를 확인해 주세요.',
    };
  if (normalized === 'ACCOUNT-REJECTED')
    return {
      title: '가입 신청이 승인되지 않았습니다.',
      detail: '소속과 신청 내용을 운영 담당자에게 확인해 주세요.',
    };
  if (normalized === 'ACCOUNT-DEPARTED')
    return {
      title: '사업장 이용이 종료된 계정입니다.',
      detail: '다시 이용해야 하는 경우 운영 담당자에게 소속과 이용 권한 확인을 요청해 주세요.',
    };
  if (normalized === 'ACCOUNT-AUTHORITY-UNAVAILABLE')
    return {
      title: '사업장 이용 권한을 확인할 수 없습니다.',
      detail: '계정의 소속 또는 권한 연결을 확인해야 합니다. 운영 담당자에게 로그인 이메일을 알려 주세요.',
    };
  if (normalized === 'ACCOUNT-REGISTRATION-INCOMPLETE')
    return {
      title: '가입 신청 정보를 확인할 수 없습니다.',
      detail: '로그인 계정은 있지만 소속 신청 기록을 찾지 못했습니다. 운영 담당자에게 로그인 이메일을 알려 주세요.',
    };
  if (normalized === 'SESSION-STATE-INVALID')
    return {
      title: '계정 상태를 다시 확인해 주세요.',
      detail: '가입·승인 상태를 확인하지 못했습니다. 다시 시도해도 같으면 운영 담당자에게 알려 주세요.',
    };
  if (normalized === 'FAILED-PRECONDITION')
    return {
      title: '계정 권한 설정이 완료되지 않았습니다.',
      detail:
        '로그인은 완료됐지만 현재 사업장 역할 또는 업무 권한을 확인할 수 없습니다. 운영 담당자에게 계정 권한 확인을 요청해 주세요.',
    };
  if (normalized === 'PERMISSION-DENIED')
    return {
      title: '이 계정에는 업무 화면 접근 권한이 없습니다.',
      detail: '승인된 사업장 역할이 있는지 운영 담당자에게 확인해 주세요.',
    };
  if (normalized === 'UNAVAILABLE' || normalized === 'DEADLINE-EXCEEDED')
    return {
      title: '업무 정보를 불러오는 데 시간이 걸리고 있습니다.',
      detail: '네트워크 상태를 확인한 뒤 다시 시도해 주세요.',
    };
  return {
    title: '계정 정보를 확인하지 못했습니다.',
    detail: '로그인은 완료됐지만 업무 권한을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  };
}

export function AuthBoundary({
  children,
  adapter: provided,
}: {
  children: (session: AuthenticatedSession, adapter: AuthAdapter) => React.ReactNode;
  adapter?: AuthAdapter;
}) {
  if (window.location.pathname === '/risk-participation')
    return <Suspense fallback={<FeatureLoading label="참여 안내 불러오는 중" />}><RiskWorkerOpinionPage /></Suspense>;
  if (window.location.pathname === '/worker-registration')
    return (
      <Suspense
        fallback={
          <main className="access-loading">
            <FeatureLoading label="근로자 등록 화면을 준비하고 있습니다." />
          </main>
        }
      >
        <WorkerRegistrationPage />
      </Suspense>
    );

  if (window.location.pathname === '/external-work')
    return (
      <Suspense
        fallback={
          <main className="access-loading">
            <FeatureLoading label="외부 작업 초대 정보를 확인하고 있습니다." />
          </main>
        }
      >
        <ExternalClaimScreen />
      </Suspense>
    );

  return <AuthenticatedBoundary adapter={provided}>{children}</AuthenticatedBoundary>;
}

function AuthenticatedBoundary({
  children,
  adapter: provided,
}: {
  children: (session: AuthenticatedSession, adapter: AuthAdapter) => React.ReactNode;
  adapter?: AuthAdapter;
}) {
  const adapter = useMemo(() => provided ?? createWorkspaceAuthAdapter(), [provided]);
  const [state, setState] = useState<AuthState>({ state: 'loading' });
  /* Two things happen while the spinner is up, and the person is told which one they are
   * waiting on rather than shown a made-up measure of how far it has got. */
  const [membershipConfirmed, setMembershipConfirmed] = useState(false);
  const activeIdentityId = useRef<string | null>(null);
  useEffect(() => {
    const stopWatchingEntitlement = adapter.observeEntitlement?.((identityId) => {
      if (activeIdentityId.current && activeIdentityId.current !== identityId)
        clearSensitiveMemoryCaches();
      activeIdentityId.current = identityId;
      setMembershipConfirmed(true);
    });
    const stopObserving = adapter.observe((next) => {
      /* Entitlement is known before canonical onboarding is complete. Starting workspace
       * reads at that earlier point used to wake every mounted feature behind the setup
       * dialog. Only a fully authenticated workspace session may warm application data. */
      if (next.state === 'authenticated') {
        const activeScope = next.scopes.find((scope) => scope.id === next.activeScopeId);
        if (activeScope && isHeadquartersScope(activeScope) && activeScope.resource.kind === 'site') {
          endHomeWarmStart();
        } else beginHomeWarmStart(next.identity.id, {
          selectedScopeId: activeScope
            ? workspaceHomeSelectedScopeId(activeScope.resource)
            : null,
          siteId: activeScope?.resource.kind === 'site' || activeScope?.resource.kind === 'contractor'
            ? activeScope.resource.siteId : null,
          includeBoard:
            window.location.pathname === '/' &&
            activeScope !== undefined &&
            activeScope.resource.kind !== 'tenant',
        });
      } else {
        clearSensitiveMemoryCaches();
        endHomeWarmStart();
        if (next.state !== 'onboarding-required') {
          activeIdentityId.current = null;
          setMembershipConfirmed(false);
        }
      }
      setState(next);
    });
    return () => {
      stopObserving();
      stopWatchingEntitlement?.();
    };
  }, [adapter]);
  useEffect(() => scheduleMidnightMemoryCacheClear(), []);

  if (state.state === 'loading')
    return (
      <main className="access-loading">
        <FeatureLoading label={membershipConfirmed ? '업무 화면을 준비하고 있습니다.' : '사용자 계정을 확인하고 있습니다.'}/>
      </main>
    );
  if (state.state === 'signed-out') return <AuthScreen adapter={adapter} />;
  if (state.state === 'registration-required')
    return <RegistrationCompletionScreen session={state} adapter={adapter} />;
  if (state.state === 'pending') return <PendingScreen session={state} adapter={adapter} />;
  /* Setup is incomplete. Keep the visual backdrop, but never mount the workspace tree:
   * `inert` blocks interaction, not effects or network reads. The onboarding dialog is the
   * only live application subtree until the canonical completion receipt exists. */
  if (state.state === 'onboarding-required') {
    return (
      <>
        <div className="setup-gate-background setup-gate-background--static" aria-hidden="true" />
        <Suspense
          fallback={
          <div className="setup-dialog-backdrop">
            <section
                className="setup-dialog setup-dialog--waiting"
                role="dialog"
                aria-modal="true"
                aria-label="사용자 설정"
              >
              <FeatureLoading label="사용자 설정을 불러오는 중" />
              </section>
            </div>
          }
        >
          <OnboardingScreen session={state} adapter={adapter} />
        </Suspense>
      </>
    );
  }
  /* The setup read failed after retrying. An unknown state is never treated as a
   * finished one; it gets its own screen with a retry rather than the workspace. */
  if (state.state === 'setup-unavailable')
    return (
      <Suspense
        fallback={
          <main className="access-loading">
            <FeatureLoading label="사용자 설정 상태를 확인하고 있습니다." />
          </main>
        }
      >
        <SetupUnavailableScreen session={state} adapter={adapter} />
      </Suspense>
    );
  if (state.state === 'session-unavailable') {
    const failure = sessionFailureCopy(state.code);
    return (
      <main className="access-loading access-loading--error">
        <section className="access-session-error" role="alert" aria-live="assertive">
          <h1>{failure.title}</h1>
          <p>{failure.detail}</p>
          <div className="access-session-error__actions">
            {normalizeSessionFailureCode(state.code) === 'UNAUTHENTICATED' ? (
              <Button onClick={() => void adapter.signOut()}>다시 로그인</Button>
            ) : (
              <Button onClick={() => window.location.reload()}>다시 시도</Button>
            )}
            <Button variant="secondary" onClick={() => void adapter.signOut()}>
              다른 계정으로 로그인
            </Button>
          </div>
        </section>
      </main>
    );
  }
  const visible = projectVisibleProductSession(state);
  return visible ? (
    <MobileSessionLock key={visible.identity.id} session={visible} adapter={adapter}>
      <WorkspaceEntry key={`${visible.identity.id}:${visible.activeScopeId}`}>{children(visible, adapter)}</WorkspaceEntry>
    </MobileSessionLock>
  ) : (
    <ProductScopeUnavailable adapter={adapter} />
  );
}

function ProductScopeUnavailable({ adapter }: { adapter: AuthAdapter }) {
  return (
    <main className="access-loading access-loading--error">
      <section className="access-session-error" role="alert">
        <h1>사용할 수 있는 사업장 업무 범위가 없습니다.</h1>
        <Button onClick={() => void adapter.signOut()}>로그아웃</Button>
      </section>
    </main>
  );
}
